import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useCurrentUser, useActiveImpersonation } from '@field-tickets/shared';
import type { ApiError } from '@field-tickets/shared';
import { useAuthStore } from '@/store/auth';
import { useImpersonationStore } from '@/store/impersonation';
import { Spinner } from './Spinner';

const DEFAULT_TAB_TITLE = 'Alturex Work';

/**
 * `/auth/me` failures fall into two buckets:
 *
 *   - Auth failures (401/403): the server is telling us this session is no
 *     longer valid. Clear local state and bounce to /login.
 *   - Transient failures (network errors, request timeouts, 5xx, 504 from a
 *     proxy that timed out an upstream): the cookie is almost certainly still
 *     valid; the response just didn't come back this time. Treating these as
 *     auth failures was the root of issue #241 — Android Chrome PWA fires a
 *     `refetchOnWindowFocus` for /auth/me when the user returns from the
 *     camera intent, and any blip on that single request (e.g. coincident
 *     with a slow multipart photo upload sharing the same connection) was
 *     enough to log the user out client-side, even though the server still
 *     considered them signed in.
 *
 * Status `0` is the conventional code our `request()` helper assigns when
 * the AbortController fires (timeout) or the network throws before a
 * response is received.
 */
function isAuthFailureError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const status = (err as ApiError).status;
  return status === 401 || status === 403;
}

export function AuthGuard() {
  const location = useLocation();
  const { setUser, setLoading, isLoading, isAuthenticated } = useAuthStore();
  const { data, isLoading: queryLoading, isError, error } = useCurrentUser();
  const setImpersonationSession = useImpersonationStore((s) => s.setSession);
  const impersonationSession = useImpersonationStore((s) => s.session);

  const status = data?.status;
  const pendingApproval = status === 'pending_approval';

  // Query impersonation status for ANY authenticated user — while impersonating,
  // the effective user is the target (non-admin), but they still need banner data.
  // Backend returns { active:false } for non-impersonated non-admins.
  const user = data?.status === 'active' ? data.user : null;
  const isAuthed = Boolean(user);
  const { data: impersonationData } = useActiveImpersonation(isAuthed);

  useEffect(() => {
    if (queryLoading) {
      setLoading(true);
      return;
    }
    // If the latest /auth/me has data, it is the source of truth for the
    // user. `data` survives a follow-up failed refetch (TanStack Query
    // preserves cached data on background errors), so seeing data here
    // means the most recent successful response is still trustworthy.
    if (data) {
      if (status === 'active') {
        setUser(data.user);
      } else {
        // pending_approval / suspended / unauthenticated — server says no.
        setUser(null);
      }
      return;
    }
    // No `data` AND the latest fetch errored. Distinguish:
    //   - Real auth failure (401/403): the server is telling us the session
    //     is invalid — clear cached user, bounce to /login.
    //   - Transient failure (network/timeout/5xx): the cookie is almost
    //     certainly still valid; preserve whatever user the store has so
    //     AuthGuard doesn't bounce a still-valid session. This is the
    //     scenario behind issue #241 (Android PWA refetches /auth/me on
    //     window focus after a camera intent; one blip = forced logout).
    if (isError) {
      if (isAuthFailureError(error)) {
        setUser(null);
      } else {
        setLoading(false);
      }
      return;
    }
    // No data, no error — should only happen pre-first-fetch. Treat as
    // logged out so we don't render protected content with stale user data.
    setUser(null);
  }, [data, status, queryLoading, isError, error, setUser, setLoading]);

  // Mirror the backend impersonation state into the Zustand store. The banner
  // only renders when `impersonating` is true — an `active` but not-yet-entered
  // session (cookie was cleared but DB row still open) is not banner-worthy.
  useEffect(() => {
    if (!isAuthed) {
      setImpersonationSession(null);
      return;
    }
    if (impersonationData?.impersonating && impersonationData.session) {
      setImpersonationSession(impersonationData.session);
    } else {
      setImpersonationSession(null);
    }
  }, [isAuthed, impersonationData, setImpersonationSession]);

  // Reflect impersonation in the browser tab title so it's visible even when
  // the banner is scrolled off or the tab is in the background.
  useEffect(() => {
    if (impersonationSession) {
      const name = impersonationSession.targetFullName || 'user';
      document.title = `[Acting as ${name}] ${DEFAULT_TAB_TITLE}`;
    } else {
      document.title = DEFAULT_TAB_TITLE;
    }
    return () => {
      document.title = DEFAULT_TAB_TITLE;
    };
  }, [impersonationSession]);

  if (isLoading || queryLoading) {
    return (
      <div className="page-loading">
        <Spinner />
      </div>
    );
  }

  if (pendingApproval) {
    return <Navigate to="/pending-approval" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}
